import { IconButton } from '@creditornot/cb-button';
import { MoreHorizontal, Pencil, PlusCircleFilled, Trashbin } from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useMutation } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { ConfirmModal } from 'common/components';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import BulkAddSubcategoryModal from 'modules/menu/categories/components/BulkAddSubcategoriesModal';
import { AddSubcategoriesFormValues } from 'modules/menu/categories/components/BulkAddSubcategoriesModal/types';
import { useAddSubcategoriesTracking, useDeleteCategoryTracking } from 'modules/menu/hooks/telemetryHooks';
import { batchCreateSubcategories, deleteCategory } from 'modules/shared-menu/api';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { BatchCreateSubcategoriesPayload, SelectionCategory } from 'modules/shared-menu/types';
import { getErrorResponseText } from 'modules/shared-menu/utils';

import EditCategoryModal from './EditCategoryModal';
import { useSelectionData } from '../../../hooks/useSelectionData';
import { useWoltSpecials } from '../context/WoltSpecialsContext';

type Props = {
  category: SelectionCategory;
  type: 'category' | 'subcategory';
};

const StyledPopover = styled(LegacyPopover).attrs({
  placement: 'bottom-start',
  renderArrow: false,
})`
  display: flex;
  align-items: center;
`;

const ToggleButton = styled(IconButton).attrs({ variant: 'brand', size: 'tiny' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 200px;
  padding: 8px;
`;

const PopoverItem = styled(MenuActionItem).attrs({ variant: 'black' })`
  & + & {
    margin-block-start: 8px;
  }
`;

const DangerPopoverItem = styled(MenuActionItem).attrs({ variant: 'red' })`
  margin-block-start: 8px;
`;

const categoryActionsTranslationVariant: { [key in Props['type']]: { [key: string]: string } } = {
  category: {
    title: 'menu.category',
    edit: 'menu.editCategory',
    delete: 'menu.deleteCategory',
    deleteDescription: 'menu.deleteCategoryDescription',
  },
  subcategory: {
    title: 'menu.subcategory.title',
    edit: 'menu.subcategory.edit',
    delete: 'menu.subcategory.delete',
    deleteDescription: 'menu.subcategory.delete-description',
  },
};

const CategoryActions = ({ category, type }: Props) => {
  const history = useHistory();
  const { menuEditorPage } = useListingManagerRoutes();
  const { selection, updateSelectionData } = useSelectionData();
  const menuFeatures = useOfferingMenuFeaturesContext();

  const trackAddSubcategories = useAddSubcategoriesTracking();
  const trackDeleteCategory = useDeleteCategoryTracking(category.id, type);
  const { woltSpecialsCategory, woltSpecialsOfferIds } = useWoltSpecials();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [isBulkAddSubcategoriesModalOpen, setIsBulkAddSubcategoriesModalOpen] = useState(false);

  const catIsWoltSpecialsAndHasOffers = useMemo(
    () => !!woltSpecialsCategory && category?.id === woltSpecialsCategory?.id && woltSpecialsOfferIds.size > 0,
    [category?.id, woltSpecialsCategory, woltSpecialsOfferIds.size],
  );

  const [show, setShow] = useState(false);

  const toggle = () => setShow(oldState => !oldState);
  const close = () => setShow(false);

  const { mutate } = useMutation(async () => deleteCategory(selection.id, category.id), {
    onError: (error: any) => {
      toast.error(getErrorResponseText(error));
    },
    onSuccess: updatedSelection => {
      toast.success(
        <FormattedMessage
          id="menu.categoryDeletedSuccess"
          values={{ name: localize(category.name, updatedSelection.primary_language) }}
        />,
      );

      trackDeleteCategory();

      history.push(menuEditorPage());
      updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
    },
  });

  const { mutateAsync: handleBulkAddSubcategory } = useMutation(
    async (subcategories: AddSubcategoriesFormValues['childCategoriesRecord'][string]) => {
      const data: BatchCreateSubcategoriesPayload[] = subcategories.map(subcategory => ({
        name: [{ lang: selection.primary_language, value: subcategory.name, verified: true }],
      }));
      const updatedSelection = await batchCreateSubcategories(selection.id, category.id, data);
      trackAddSubcategories(
        category.id,
        subcategories.map(({ name }) => name),
      );
      updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
    },
    {
      onError: (error: any) => {
        toast.error(getErrorResponseText(error));
      },
      onSettled: () => setIsBulkAddSubcategoriesModalOpen(false),
    },
  );

  return (
    <>
      <StyledPopover
        content={
          <PopoverContent>
            <MenuItemGroup title={<FormattedMessage id={categoryActionsTranslationVariant[type].title} />}>
              <PopoverItem icon={<Pencil />} onClick={() => setIsEditCategoryModalOpen(true)}>
                <FormattedMessage id={categoryActionsTranslationVariant[type].edit} />
              </PopoverItem>
              {type !== 'subcategory' && category.id !== woltSpecialsCategory?.id && menuFeatures.isRetail && (
                <PopoverItem
                  disabled={!!category.offer_bindings?.length}
                  icon={<PlusCircleFilled />}
                  onClick={() => setIsBulkAddSubcategoriesModalOpen(true)}
                >
                  <FormattedMessage id="menu.subcategory.add-option" />
                </PopoverItem>
              )}
              <DangerPopoverItem
                disabled={catIsWoltSpecialsAndHasOffers}
                icon={<Trashbin />}
                onClick={() => setIsDeleteModalOpen(true)}
              >
                <FormattedMessage id={categoryActionsTranslationVariant[type].delete} />
              </DangerPopoverItem>
            </MenuItemGroup>
          </PopoverContent>
        }
        onClickOutside={close}
        onClickInside={close}
        show={show}
      >
        <ToggleButton tabIndex={-1} onClick={toggle}>
          <MoreHorizontal />
        </ToggleButton>
      </StyledPopover>
      <ConfirmModal
        description={
          <T.Body3>
            <FormattedMessage id={categoryActionsTranslationVariant[type].deleteDescription} />
          </T.Body3>
        }
        header={<FormattedMessage id={categoryActionsTranslationVariant[type].delete} />}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={() => mutate()}
        open={isDeleteModalOpen}
      />
      <EditCategoryModal
        type={type}
        category={category}
        isOpen={isEditCategoryModalOpen}
        onClose={() => setIsEditCategoryModalOpen(false)}
      />
      <BulkAddSubcategoryModal
        onCancel={() => setIsBulkAddSubcategoriesModalOpen(false)}
        onConfirm={handleBulkAddSubcategory}
        isOpen={isBulkAddSubcategoriesModalOpen}
      />
    </>
  );
};

export default CategoryActions;
